import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCurrentFocusKey, setFocus } from '@noriginmedia/norigin-spatial-navigation';

import Modal from '@/components/Modal/Modal';

import { Language } from '@/services/graphQL/api/generated/types_web';
import { Keys, getSystem } from '@/system';
import { getLocalization } from '@/utils/localization';

type UseExitAppOptions = {
  userLanguage: Language;
  variant?: 'destructive' | 'default';
  enableBackListener?: boolean;
  shouldOpenOnBack?: () => boolean;
  onBack?: () => void;
  onCancel?: () => void;
};

type UseExitAppReturn = {
  openExitModal: () => void;
  ExitModal: JSX.Element | null;
  exitModalOpen: boolean;
};

export function useExitApp(options: UseExitAppOptions): UseExitAppReturn {
  const {
    userLanguage,
    variant = 'destructive',
    enableBackListener = false,
    shouldOpenOnBack = () => true,
    onBack = () => {},
    onCancel = () => {},
  } = options;

  const [showExitModal, setShowExitModal] = useState(false);
  const [previousFocusKey, setPreviousFocusKey] = useState<string | null>(null);
  const system = getSystem();

  const exitOverlayLocalization = useMemo(() => {
    return getLocalization(userLanguage)['exit-overlay'];
  }, [userLanguage]);

  const closeExitModal = useCallback(() => {
    setShowExitModal(false);
    onCancel?.();
    if (previousFocusKey) {
      setFocus(previousFocusKey);
    }
  }, [previousFocusKey]);

  const exitApp = useCallback(() => {
    setShowExitModal(false);
    system.closeApp();
  }, [system]);

  const openExitModal = useCallback(() => {
    if (system.exitDirectly) {
      system.closeApp();
      return;
    }
    const currentFocusKey = getCurrentFocusKey();
    setPreviousFocusKey(currentFocusKey);
    setShowExitModal(true);
  }, [system]);

  useEffect(() => {
    if (!enableBackListener) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const key = system.key(event);
      if (key === Keys.BACK) {
        if (shouldOpenOnBack()) {
          openExitModal();
          event.preventDefault();
          event.stopImmediatePropagation();
        } else if (onBack) {
          onBack();
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [enableBackListener, shouldOpenOnBack, onBack, openExitModal, system]);

  const ExitModal =
    showExitModal && !system.disableExitPrompt ? (
      <Modal
        title={exitOverlayLocalization.title}
        description={exitOverlayLocalization.description}
        saveText={exitOverlayLocalization.yesText}
        onSave={exitApp}
        cancelText={exitOverlayLocalization.cancelText}
        onCancel={closeExitModal}
        closeModal={closeExitModal}
        userLanguage={userLanguage}
        variant={variant}
      />
    ) : null;

  return {
    openExitModal,
    ExitModal,
    exitModalOpen: showExitModal,
  };
}

export default useExitApp;
